import { Box, Divider, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { FilterCheckbox } from "@/components/common/Filters"
import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { AerosDatePicker } from "@/components/UiKit"
import type { PriceChangeHistoryFilterInput } from "@/graphql/codegen/graphql"
import { ProductManagerFilter } from "@/screens/Pricing/components/filters/ProductManagerFilter"
import { SELLING_PRICE_STATUS_OPTIONS } from "@/screens/Pricing/constants"

type FormValues = PriceChangeHistoryFilterInput & {
  assignedToIds?: string[]
  status?: string[]
  dateFrom?: string
  dateTo?: string
}

interface FilterDrawerProps {
  showFilterDrawer: boolean
  toggleDrawer: (open: boolean) => void
}

const SellingPriceHistoryFilterDrawer: FC<FilterDrawerProps> = ({ toggleDrawer, showFilterDrawer }) => {
  const currentSearch = useSearch({
    from: "/app/pricing/_layout/history/price-change",
    strict: true,
  })

  const navigate = useNavigate({
    from: "/app/pricing/history/price-change",
  })

  const { assignedToIds = [], dateFrom, dateTo, status = [], changeType } = currentSearch

  const isClearButtonEnabled =
    Boolean(assignedToIds.length) || Boolean(status.length) || Boolean(dateFrom) || Boolean(dateTo)
  const methods = useForm<FormValues>({
    values: {
      assignedToIds,
      dateFrom,
      dateTo,
      status,
      changeType,
    },
  })

  const handleFilterChange = async (name: string, value?: string | string[]) => {
    await navigate({
      search: (prev) => {
        const val = Array.isArray(value) && value.length === 0 ? undefined : value

        return {
          ...prev,
          [name]: val || undefined,
        }
      },
      replace: true,
    })
  }

  const handleFilterClear = async () => {
    await navigate({
      search: (prev) => {
        return {
          ...prev,
          assignedToIds: undefined,
          dateFrom: undefined,
          dateTo: undefined,
          status: undefined,
        }
      },
      replace: true,
    })
  }

  return (
    <SearchFilterDrawer
      open={showFilterDrawer}
      toggleDrawer={() => toggleDrawer(!showFilterDrawer)}
      clear={handleFilterClear}
      isClearButtonEnabled={isClearButtonEnabled}
      testId='price-change-history.filter-drawer'
    >
      <FormProvider {...methods}>
        <Box className='flex h-full flex-col gap-4 overflow-y-auto'>
          <Box>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Date Changed
            </Typography>
            <Divider className='mb-4' />
            <Box className='flex flex-col gap-4 px-4'>
              <AerosDatePicker
                label='From'
                value={dateFrom ?? ""}
                onChange={(value) => handleFilterChange("dateFrom", value ?? undefined)}
                data-testid='price-change-history.date-range-filter.date-from-field'
              />
              <AerosDatePicker
                label='To'
                value={dateTo ?? ""}
                onChange={(value) => handleFilterChange("dateTo", value ?? undefined)}
                data-testid='price-change-history.date-range-filter.date-to-field'
              />
            </Box>
          </Box>

          <ProductManagerFilter onFilterChange={handleFilterChange} />

          <Box>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Status
            </Typography>

            <Divider className='mb-4' />

            <Box className='flex h-[224px] flex-col overflow-y-auto px-4'>
              {SELLING_PRICE_STATUS_OPTIONS.map((option) => (
                <FilterCheckbox
                  key={option.value}
                  control={methods.control}
                  name='status'
                  value={option.value}
                  values={status}
                  label={option.label}
                  onFilterChange={(value) => handleFilterChange("status", value)}
                  data-testid={`price-change-history.status-filter.filter-option-${option.value}`}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </FormProvider>
    </SearchFilterDrawer>
  )
}

export default SellingPriceHistoryFilterDrawer
